import React, { FC, useMemo } from 'react'
import classNames from 'classnames'
import { Banner } from '@/molecules/TicketsBanner'
import { useGuildUser } from '@/services/GuildUserService'
import { formatBannerViewModel } from '@/services/UltrawideBannerService'
import { UltrawideBanner } from '@/types/codegen-contentful'
import { useTranslate } from '@/utils/translate/translate-client'
import { ReleaseColumn } from '@/views/TicketsLandingView/ReleaseColumn'
import { TicketsLandingRelease } from '@/views/TicketsLandingView/types'

interface Props {
  upcoming: TicketsLandingRelease[]
  inTheaters: TicketsLandingRelease[]
  pastReleases: TicketsLandingRelease[]
  banner?: UltrawideBanner
}

interface Grouping {
  releases: TicketsLandingRelease[]
  heading: string
  subHeading: string
}

export const TicketsLandingViewOld: FC<Props> = ({ banner, upcoming, inTheaters, pastReleases }) => {
  const guildUser = useGuildUser()
  const { t } = useTranslate('tickets')

  const upcomingWithTBA = upcoming

  const bannerModel = useMemo(() => {
    return banner ? formatBannerViewModel(banner as UltrawideBanner, guildUser.isGuildMember) : undefined
  }, [banner, guildUser])

  const groupings: Grouping[] = [
    {
      releases: inTheaters,
      heading: t('inTheaters', 'In Theaters'),
      subHeading: t('getShowtimesNow', 'Get showtimes to these films now!'),
    },
    {
      releases: upcomingWithTBA,
      heading: t('upcomingReleases', 'Upcoming Releases'),
      subHeading: t('learnMoreAboutTheseMovies', 'Learn more about these can’t-miss movies. '),
    },
    {
      releases: pastReleases,
      heading: t('pastReleases', 'Past Releases'),
      subHeading: t('streamFromYourHomeToday', 'Stream from the comfort of your home today.'),
    },
  ]

  return (
    <main className="min-h flex flex-col items-center bg-core-gray-950 px-4 py-12 text-center text-white">
      <div className="h-full w-full">{bannerModel && <Banner className="mt-10" banner={bannerModel} />}</div>
      <section className={classNames('flex flex-col items-center bg-core-gray-950 px-4 py-12 text-center text-white')}>
        <div className=" mb-12 flex flex-col items-center lg:w-full lg:flex-row lg:items-start lg:justify-center lg:gap-20">
          {groupings.map(
            (grouping) =>
              hasReleases(grouping.releases) && (
                <ReleaseColumn
                  key={grouping.heading}
                  heading={grouping.heading}
                  subHeading={grouping.subHeading}
                  releases={grouping.releases}
                />
              ),
          )}
        </div>
      </section>
    </main>
  )
}

function hasReleases(releases: TicketsLandingRelease[]): boolean {
  return releases.length > 0
}
